// Phase 4.1 — Billing "View Billing" detail panel.
//
// Replaces the MUI <Drawer> invoice-details view in billing.tsx with a shadcn
// <Sheet>. Presentation only: the invoice/customer data and the PDF print action
// are the screen's (getInvoiceDetails / getCustomerDetails / onPrint) — the
// @react-pdf receipt generation is untouched. Read-only view, no form.
import * as React from "react";
import { Pencil, Printer } from "lucide-react";
import moment from "moment";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "ui/sheet";
import { Button } from "ui/button";
import { cn } from "lib/utils";
import BillNavigator from "./BillNavigator";
import type { BillNavigation } from "./useBillNavigation";
import AppConstant from "../../Globals/AppConstants";
import { mergeDuplicateProductLines, computeRoundedTotal } from "../../Utils/helpers";

export interface ViewBillingSheetProps {
  open: boolean;
  onClose: () => void;
  customerDetails: any;
  invoiceDetails: any[];
  callGetInvoice: boolean;
  onPrint: () => void;
  /** Optional prev/next stepping across the billing list — see useBillNavigation.
      Omitted by callers that have no surrounding list to step through. */
  navigation?: BillNavigation;
  /** Optional — when supplied, an "Edit" action is shown next to Print that hands
      this bill off to the Create/Edit Bill screen. Omitted by callers with no
      edit route for the invoice (e.g. the customer credit ledger). */
  onEdit?: () => void;
}

export function ViewBillingSheet({
  open,
  onClose,
  customerDetails,
  invoiceDetails,
  callGetInvoice,
  onPrint,
  navigation,
  onEdit,
}: ViewBillingSheetProps) {
  const mergedInvoiceDetails = React.useMemo(() => mergeDuplicateProductLines(invoiceDetails), [invoiceDetails]);
  // Recomputed via the same shared round-off rule Create/Edit Bill uses,
  // rather than trusting the stored `totalamount` verbatim — guarantees this
  // always matches Create/Edit Bill and the printed receipt.
  const totalBillAmount = callGetInvoice
    ? computeRoundedTotal(invoiceDetails?.[0]?.amount, invoiceDetails?.[0]?.roundOff)
    : null;
  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-b px-5 py-4">
          {/* pr-10 keeps the navigator clear of the Sheet's own close (X) button. */}
          <div className="flex items-center justify-between gap-3 pr-10">
            <SheetTitle>{AppConstant?.viewBilling}</SheetTitle>
            {navigation && <BillNavigator {...navigation} />}
          </div>
        </SheetHeader>

        {/* overflow-hidden (not -auto): the item list below is the only region
            that scrolls, via its own flex-1 + min-h-0, so the summary above and
            totals below stay fixed in place and are never pushed off-screen or
            covered by the list — no nested/competing scrollbars. */}
        <div className="flex flex-1 flex-col gap-4 overflow-hidden p-5">
          {/* Customer / invoice summary + print */}
          <div className="flex shrink-0 items-start justify-between gap-4">
            <div className="space-y-1">
              {/* customerDetails comes from a billing.find() against the already-loaded
                  list and can miss/lag; invoiceDetails[0] is fetched fresh for this
                  exact invoiceId, so it's the more reliable fallback for both fields. */}
              <div className="font-medium text-foreground">
                Bill No - {customerDetails?.invoiceId ?? (callGetInvoice ? invoiceDetails?.[0]?.invoiceId : undefined) ?? "-"}
              </div>
              <div className="text-sm text-muted-foreground">
                Date - {(() => {
                  const date = customerDetails?.date ?? (callGetInvoice ? invoiceDetails?.[0]?.date : undefined);
                  return date ? moment(date).format("DD-MMM-YYYY") : "-";
                })()}
              </div>
              <div className="text-sm text-muted-foreground">{customerDetails?.customerName}</div>
              <div className="text-sm text-muted-foreground">{customerDetails?.mobileNumberList}</div>
              <div className="text-sm text-muted-foreground">
                Item Count - {customerDetails?.itemsCount ?? (callGetInvoice ? mergedInvoiceDetails?.length : undefined) ?? "-"}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {onEdit && (
                <Button variant="outline" size="sm" onClick={onEdit}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {AppConstant?.edit}
                </Button>
              )}
              <Button variant="outline" size="icon" aria-label="Print receipt" onClick={onPrint}>
                <Printer className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Product line items — header stays put; body scrolls internally
              once it passes max-h, so the box hugs its content (no big empty
              gap for a 1-item bill) but still caps out and scrolls for a long
              one, keeping every row reachable without touching the summary or
              totals. */}
          <div className="flex max-h-[360px] flex-col overflow-hidden rounded-lg border">
            <div className="grid grid-cols-6 gap-2 bg-muted px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <div>Barcode No</div>
              <div>Product Name</div>
              <div>Quantity</div>
              <div>MRP</div>
              <div>Discount</div>
              <div>Selling Price</div>
            </div>
            <div className="overflow-y-auto">
              {mergedInvoiceDetails.map((item: any, index: number) => {
                // Same gross-minus-discount math (and no line-level rounding)
                // as the Create Bill screen's own Line Total column, using the
                // exact grossTotal/discountAmount the merge above already
                // summed — so this matches Create Bill and the printed
                // receipt (which derives the same total) exactly.
                const lineTotal = item?.grossTotal - item?.discountAmount;
                return (
                  <div
                    key={item?.productId}
                    className={cn("grid grid-cols-6 gap-2 px-3 py-2 text-sm", (index + 1) % 2 === 0 ? "bg-muted/40" : "bg-transparent")}
                  >
                    <div className="min-w-0 break-words">{item?.productId}</div>
                    <div className="min-w-0 break-words">{item?.productName}</div>
                    <div className="tabular-nums">{item?.itemsCount}</div>
                    <div className="text-right tabular-nums">Rs.{item?.mrp}</div>
                    <div className="text-right tabular-nums">{item?.discountPercentage != null ? `${Number(item.discountPercentage).toFixed(2)}%` : "0%"}</div>
                    <div className="text-right tabular-nums">Rs.{Number(lineTotal).toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Totals — always visible, never scrolled with the item list. */}
          <div className="shrink-0 space-y-2 border-t pt-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{AppConstant?.totalAmount}</span>
              <span className="tabular-nums">Rs. {callGetInvoice ? invoiceDetails[0]?.amount : null}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Additional Discount</span>
              <span className="tabular-nums">{callGetInvoice ? invoiceDetails[0]?.roundOff : null}</span>
            </div>
            <div className="flex items-center justify-between border-t pt-2 font-medium text-foreground">
              <span>{AppConstant?.totalBillAmount}</span>
              <span className="tabular-nums">Rs.{totalBillAmount}</span>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ViewBillingSheet;
